import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Trash2 } from 'lucide-react';

import { FlexColumn } from 'src/components/flex';
import Select from 'src/components/input/select/Select';
import CreateOrUpdatePropertyModal from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal';
import { PROPERTY_INTEGRATIONS_ANCHOR_ID } from 'src/components/create-or-update-property-modal/use-property-section-navigation';
import type { ITourIntegration } from 'src/services/api/properties/types';
import { useProperties } from 'src/services/api';
import { useProperty, type IPropertyWithIntegrations } from 'src/services/api/properties/use-property';
import { useTourIntegrationMutations } from 'src/services/api/properties/use-tour-integration-mutations';

import styles from 'src/components/create-or-update-property-modal/PropertyFactsSection.module.css';
import integrationStyles from 'src/components/create-or-update-property-modal/PropertyIntegrationsSection.module.css';

interface Props {
    property: IPropertyWithIntegrations;
}

const INTEGRATION_PROVIDER_OPTIONS = [
    { value: 'elise_ai', label: 'Elise.ai' },
];

const INTEGRATION_PROVIDER_LABELS: Record<string, string> = {
    elise_ai: 'Elise.ai',
};

interface IntegrationFormRow {
    integrationId: number | null;
    provider: string | null;
    externalId: string;
    originalExternalId: string;
    buildingSlug: string;
    originalBuildingSlug: string;
    orgSlug: string;
    originalOrgSlug: string;
    // write-only: never populated from the server response. Blank means "leave
    // the stored security key unchanged" on an existing integration.
    securityKey: string;
    hasSecurityKey: boolean;
    deleted: boolean;
}

type IntegrationOperation =
    | {
          type: 'create';
          provider: string;
          externalId: string | null;
          buildingSlug: string | null;
          orgSlug: string | null;
          securityKey: string | null;
      }
    | {
          type: 'update';
          integrationId: number;
          externalId: string | null;
          buildingSlug: string | null;
          orgSlug: string | null;
          securityKey: string | null;
      }
    | { type: 'delete'; integrationId: number };

function integrationToFormRow(integration: ITourIntegration): IntegrationFormRow {
    const externalId = integration.external_property_id ?? '';
    const buildingSlug = integration.custom_integration_data?.building_slug ?? '';
    const orgSlug = integration.custom_integration_data?.org_slug ?? '';
    return {
        integrationId: integration.id,
        provider: integration.provider,
        externalId,
        originalExternalId: externalId,
        buildingSlug,
        originalBuildingSlug: buildingSlug,
        orgSlug,
        originalOrgSlug: orgSlug,
        securityKey: '',
        hasSecurityKey: Boolean(integration.has_security_key),
        deleted: false,
    };
}

function getDefaultRows(property: IPropertyWithIntegrations): IntegrationFormRow[] {
    return (property.integrations?.integrations ?? []).map(integrationToFormRow);
}

type IntegrationFieldName = 'externalId' | 'buildingSlug' | 'orgSlug' | 'securityKey';
type IntegrationRowErrors = Partial<Record<IntegrationFieldName, string>>;

/** True when this row will actually produce a create/update operation on save --
 * a new row always will, but an existing row only if something about it changed.
 * Untouched legacy rows (e.g. missing fields from before these became required)
 * must not be validated or saved. */
function rowWillBeSaved(row: IntegrationFormRow): boolean {
    if (row.deleted || !row.provider) {
        return false;
    }
    if (row.integrationId == null) {
        return true;
    }
    return (
        row.externalId !== row.originalExternalId ||
        row.buildingSlug !== row.originalBuildingSlug ||
        row.orgSlug !== row.originalOrgSlug ||
        row.securityKey !== ''
    );
}

function validateRow(row: IntegrationFormRow): IntegrationRowErrors {
    const errors: IntegrationRowErrors = {};
    if (!row.externalId.trim()) {
        errors.externalId = 'Building ID is required';
    }
    if (!row.buildingSlug.trim()) {
        errors.buildingSlug = 'Building Slug is required';
    }
    if (!row.orgSlug.trim()) {
        errors.orgSlug = 'Org Slug is required';
    }
    // a blank Security Key is only an error when the row has never had one set --
    // otherwise blank means "leave the stored key unchanged"
    if (!row.securityKey.trim() && !row.hasSecurityKey) {
        errors.securityKey = 'Security Key is required';
    }
    return errors;
}

export default function PropertyIntegrationsSection({ property }: Props) {
    const { create, update, remove } = useTourIntegrationMutations(property.id);
    const { data: properties = [] } = useProperties();
    const [isEditingParent, setIsEditingParent] = useState(false);

    const isReadOnly = property.parent_property_id != null;
    const sourceProperty = properties.find(
        (candidate) => candidate.id === (
            property.integrations?.source_property_id ?? property.parent_property_id
        )
    );
    const { data: sourcePropertyForEditing } = useProperty(
        isEditingParent ? (sourceProperty?.id ?? null) : null,
        { include_facts: true, include_integrations: true, include_touring_hours: true },
        // this data feeds an editable CreateOrUpdatePropertyModal — a background
        // refetch on window focus would silently overwrite unsaved edits
        false,
    );

    const defaultRows = useMemo(() => getDefaultRows(property), [property.id, property.integrations]);
    const [rows, setRows] = useState<IntegrationFormRow[]>(defaultRows);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<number, IntegrationRowErrors>>({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setRows(defaultRows);
        setSaveError(null);
        setFieldErrors({});
    }, [defaultRows]);

    const visibleRows = rows.filter((row) => !row.deleted);
    const reservedProviders = new Set(
        visibleRows.flatMap((row) => (row.provider ? [row.provider] : []))
    );
    const availableProviderOptions = INTEGRATION_PROVIDER_OPTIONS.filter(
        (option) => !reservedProviders.has(option.value)
    );
    const canAddRow = availableProviderOptions.length > 0 &&
        visibleRows.every((row) => Boolean(row.provider));

    const hasUnsavedChanges = rows.some((row) => {
        if (row.integrationId == null) {
            // a new row with no provider selected yet has nothing to save
            return !row.deleted && Boolean(row.provider);
        }
        return (
            row.deleted ||
            row.externalId !== row.originalExternalId ||
            row.buildingSlug !== row.originalBuildingSlug ||
            row.orgSlug !== row.originalOrgSlug ||
            row.securityKey !== ''
        );
    });

    const handleAddRow = () => {
        setSaveError(null);
        setRows((prev) => [
            ...prev,
            {
                integrationId: null,
                provider: null,
                externalId: '',
                originalExternalId: '',
                buildingSlug: '',
                originalBuildingSlug: '',
                orgSlug: '',
                originalOrgSlug: '',
                securityKey: '',
                hasSecurityKey: false,
                deleted: false,
            },
        ]);
    };

    const handleDeleteRow = (index: number) => {
        setSaveError(null);
        const row = rows[index];
        if (!row) {
            return;
        }
        if (row.integrationId == null) {
            setRows((prev) => prev.filter((_, i) => i !== index));
            return;
        }
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, deleted: true } : r)));
    };

    const clearFieldError = (index: number, field: IntegrationFieldName) => {
        setFieldErrors((prev) => {
            if (!prev[index]?.[field]) {
                return prev;
            }
            const rest = { ...prev[index] };
            delete rest[field];
            return { ...prev, [index]: rest };
        });
    };

    const handleProviderChange = (index: number, provider: string) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, provider } : r)));
    };

    const handleExternalIdChange = (index: number, externalId: string) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, externalId } : r)));
        clearFieldError(index, 'externalId');
    };

    const handleBuildingSlugChange = (index: number, buildingSlug: string) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, buildingSlug } : r)));
        clearFieldError(index, 'buildingSlug');
    };

    const handleOrgSlugChange = (index: number, orgSlug: string) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, orgSlug } : r)));
        clearFieldError(index, 'orgSlug');
    };

    const handleSecurityKeyChange = (index: number, securityKey: string) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, securityKey } : r)));
        clearFieldError(index, 'securityKey');
    };

    const applyChanges = async () => {
        setSaveError(null);

        const nextFieldErrors: Record<number, IntegrationRowErrors> = {};
        rows.forEach((row, index) => {
            if (!rowWillBeSaved(row)) {
                return;
            }
            const rowErrors = validateRow(row);
            if (Object.keys(rowErrors).length > 0) {
                nextFieldErrors[index] = rowErrors;
            }
        });
        setFieldErrors(nextFieldErrors);
        if (Object.keys(nextFieldErrors).length > 0) {
            setSaveError('Please fix the highlighted fields before saving');
            return false;
        }

        const operations = rows.flatMap((row): IntegrationOperation[] => {
            if (row.integrationId != null && row.deleted) {
                return [{ type: 'delete', integrationId: row.integrationId }];
            }
            if (!rowWillBeSaved(row)) {
                return [];
            }
            if (row.integrationId == null) {
                return [
                    {
                        type: 'create',
                        provider: row.provider as string,
                        externalId: row.externalId || null,
                        buildingSlug: row.buildingSlug || null,
                        orgSlug: row.orgSlug || null,
                        securityKey: row.securityKey || null,
                    },
                ];
            }
            return [
                {
                    type: 'update',
                    integrationId: row.integrationId,
                    externalId: row.externalId || null,
                    buildingSlug: row.buildingSlug || null,
                    orgSlug: row.orgSlug || null,
                    securityKey: row.securityKey || null,
                },
            ];
        });

        if (operations.length === 0) {
            return true;
        }

        setIsSaving(true);
        try {
            const results = await Promise.allSettled(operations.map((op) => {
                switch (op.type) {
                    case 'create':
                        return create.mutateAsync({
                            provider: op.provider,
                            externalId: op.externalId,
                            buildingSlug: op.buildingSlug,
                            orgSlug: op.orgSlug,
                            securityKey: op.securityKey,
                        });
                    case 'update':
                        return update.mutateAsync({
                            integrationId: op.integrationId,
                            externalId: op.externalId,
                            buildingSlug: op.buildingSlug,
                            orgSlug: op.orgSlug,
                            securityKey: op.securityKey,
                        });
                    case 'delete':
                        return remove.mutateAsync({ integrationId: op.integrationId });
                }
            }));

            const errors = results.flatMap((result) => (result.status === 'rejected' ? [result.reason] : []));
            if (errors.length > 0) {
                setSaveError('Failed to save integration changes');
                return false;
            }

            return true;
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <FlexColumn rowGap={2} id={PROPERTY_INTEGRATIONS_ANCHOR_ID}>
            <div className={styles.header}>
                <Typography fontWeight={500} data-property-section-title>Integrations</Typography>
                {!isReadOnly && hasUnsavedChanges && (
                    <Typography variant="caption" color="text.secondary" data-testid="property-integrations-dirty-indicator">
                        Unsaved changes
                    </Typography>
                )}
            </div>

            {isReadOnly && (
                <Alert severity="info" data-testid="property-integrations-inherited-alert">
                    These integrations are inherited from{' '}
                    {sourceProperty ? (
                        <Button
                            type="button"
                            variant="text"
                            onClick={() => setIsEditingParent(true)}
                            className={`${styles.inheritedParentLink} ellipsisText`}
                            title={sourceProperty.name}
                            data-testid="edit-parent-integrations-link"
                        >
                            {sourceProperty.name}
                        </Button>
                    ) : (
                        'the parent property'
                    )}
                    . Edit them on the parent to change this property&rsquo;s integrations.
                </Alert>
            )}

            {isEditingParent && sourcePropertyForEditing && (
                <CreateOrUpdatePropertyModal
                    opened={isEditingParent}
                    onClose={() => setIsEditingParent(false)}
                    property={sourcePropertyForEditing}
                    initialSection="tourSettings"
                    scrollToAnchorId={PROPERTY_INTEGRATIONS_ANCHOR_ID}
                />
            )}

            {saveError && (
                <Alert severity="error" data-testid="property-integrations-error-alert">
                    {saveError}
                </Alert>
            )}

            {rows.map((row, index) => {
                if (row.deleted) {
                    return null;
                }
                const isNewRow = row.integrationId == null;
                const rowTestId = row.provider ?? `row-${index}`;

                return (
                    <div
                        key={row.integrationId ?? `new-${index}`}
                        className={integrationStyles.integrationRow}
                    >
                        <div className={integrationStyles.integrationRowHeader}>
                            {isNewRow ? (
                                <Select
                                    label="Integration"
                                    placeholder="Select integration"
                                    options={availableProviderOptions}
                                    value={row.provider}
                                    onChange={(value) => handleProviderChange(index, value)}
                                    size="small"
                                    fullWidth
                                    disabled={isReadOnly}
                                    data-testid={`property-integration-provider-${rowTestId}`}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary" className={styles.factLabel}>
                                    {INTEGRATION_PROVIDER_LABELS[row.provider ?? ''] ?? row.provider}
                                </Typography>
                            )}
                            <IconButton
                                type="button"
                                size="small"
                                onClick={() => handleDeleteRow(index)}
                                disabled={isReadOnly}
                                data-testid={isNewRow ? `delete-new-integration-${rowTestId}` : `delete-integration-${row.provider}`}
                            >
                                <Trash2 size={16} />
                            </IconButton>
                        </div>
                        <div className={integrationStyles.integrationFieldsGrid}>
                            <TextField
                                label="Building ID"
                                value={row.externalId}
                                onChange={(event) => handleExternalIdChange(index, event.target.value)}
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                placeholder="Not set"
                                InputLabelProps={{ shrink: true }}
                                error={!!fieldErrors[index]?.externalId}
                                helperText={fieldErrors[index]?.externalId}
                                data-testid={`property-integration-external-id-${rowTestId}`}
                            />
                            <TextField
                                label="Building Slug"
                                value={row.buildingSlug}
                                onChange={(event) => handleBuildingSlugChange(index, event.target.value)}
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                placeholder="Not set"
                                InputLabelProps={{ shrink: true }}
                                error={!!fieldErrors[index]?.buildingSlug}
                                helperText={fieldErrors[index]?.buildingSlug}
                                data-testid={`property-integration-building-slug-${rowTestId}`}
                            />
                            <TextField
                                label="Org Slug"
                                value={row.orgSlug}
                                onChange={(event) => handleOrgSlugChange(index, event.target.value)}
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                placeholder="Not set"
                                InputLabelProps={{ shrink: true }}
                                error={!!fieldErrors[index]?.orgSlug}
                                helperText={fieldErrors[index]?.orgSlug}
                                data-testid={`property-integration-org-slug-${rowTestId}`}
                            />
                            <TextField
                                label="Security Key"
                                type="password"
                                value={row.securityKey}
                                onChange={(event) => handleSecurityKeyChange(index, event.target.value)}
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                placeholder={row.hasSecurityKey ? '••••••••' : 'Not set'}
                                InputLabelProps={{ shrink: true }}
                                error={!!fieldErrors[index]?.securityKey}
                                helperText={fieldErrors[index]?.securityKey}
                                data-testid={`property-integration-security-key-${rowTestId}`}
                            />
                        </div>
                    </div>
                );
            })}

            {!isReadOnly && (
                <Button
                    type="button"
                    variant="outlined"
                    size="medium"
                    fullWidth
                    onClick={handleAddRow}
                    disabled={!canAddRow}
                    className={styles.addRowButton}
                    data-testid="add-property-integration-row-button"
                >
                    + Add Integration
                </Button>
            )}

            {!isReadOnly && (
                <div className={styles.actions}>
                    <LoadingButton
                        type="button"
                        className={styles.saveButton}
                        variant="contained"
                        size="small"
                        loading={isSaving}
                        disabled={!hasUnsavedChanges}
                        onClick={applyChanges}
                        data-testid="save-property-integrations-button"
                    >
                        Save
                    </LoadingButton>
                </div>
            )}
        </FlexColumn>
    );
}
